import React, { useState, useEffect } from 'react';
import { Building2, Plus, RefreshCw, Edit2, Trash2 } from 'lucide-react';
import { dealerService } from '../services/dealer.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';

const DEALER_TYPES = ['SERVICE_CENTER', 'TOWING', 'MULTI_SERVICE', 'OTHER'];

const emptyForm = {
  dealer_code: '',
  dealer_name: '',
  dealer_type: 'SERVICE_CENTER',
  contact_person: '',
  contact_number: '',
  email: '',
  gst_number: '',
  pan_number: '',
  business_license: '',
  supported_brands: '',
  services_offered: '',
  address: '',
  is_active: true,
};

function DealerModal({ isOpen, onClose, onSave, dealer }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (dealer) {
      setForm({
        dealer_code: dealer.dealer_code || '',
        dealer_name: dealer.dealer_name || '',
        dealer_type: dealer.dealer_type || 'SERVICE_CENTER',
        contact_person: dealer.contact_person || '',
        contact_number: dealer.contact_number || '',
        email: dealer.email || '',
        gst_number: dealer.gst_number || '',
        pan_number: dealer.pan_number || '',
        business_license: dealer.business_license || '',
        supported_brands: (dealer.supported_brands || []).join(', '),
        services_offered: (dealer.services_offered || []).join(', '),
        address: dealer.address?.address || '',
        is_active: dealer.is_active !== false,
      });
    } else {
      setForm(emptyForm);
    }
    setError('');
  }, [dealer, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        supported_brands: form.supported_brands.split(',').map(s => s.trim()).filter(Boolean),
        services_offered: form.services_offered.split(',').map(s => s.trim()).filter(Boolean),
        address: form.address ? { address: form.address } : null,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save dealer.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {dealer ? 'Edit Dealer' : 'New Dealer'}
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Code *</label>
              <input name="dealer_code" value={form.dealer_code} onChange={handleChange} required
                disabled={!!dealer}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Name *</label>
              <input name="dealer_name" value={form.dealer_name} onChange={handleChange} required
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dealer Type</label>
              <select name="dealer_type" value={form.dealer_type} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                {DEALER_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
              <input name="contact_person" value={form.contact_person} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
              <input name="contact_number" value={form.contact_number} onChange={handleChange} required
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GST Number (IRD)</label>
              <input name="gst_number" value={form.gst_number} onChange={handleChange}
                placeholder="8–9 digit IRD number"
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IRD Number</label>
              <input name="pan_number" value={form.pan_number} onChange={handleChange}
                placeholder="8–9 digit IRD number"
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input name="address" value={form.address} onChange={handleChange}
              placeholder="Full address"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supported Brands (comma-separated)</label>
            <input name="supported_brands" value={form.supported_brands} onChange={handleChange}
              placeholder="Toyota, Mazda, Ford"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Services Offered (comma-separated)</label>
            <input name="services_offered" value={form.services_offered} onChange={handleChange}
              placeholder="Towing, Battery, Tyres"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" name="is_active" id="is_active" checked={form.is_active} onChange={handleChange} />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Active</label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const DealerMaintenance = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  useEffect(() => {
    fetchDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchDealers = async () => {
    setLoading(true);
    try {
      const data = await dealerService.getDealers({ page, limit: LIMIT, search: search || undefined });
      setDealers(data.items || data || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching dealers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchDealers();
  };

  const handleCreate = () => { setEditingDealer(null); setShowModal(true); };
  const handleEdit = (d) => { setEditingDealer(d); setShowModal(true); };

  const handleSave = async (payload) => {
    if (editingDealer) {
      await dealerService.updateDealer(editingDealer.id, payload);
    } else {
      await dealerService.createDealer(payload);
    }
    fetchDealers();
  };

  const confirmDelete = async () => {
    try {
      await dealerService.deleteDealer(deleteId);
      setDeleteId(null);
      fetchDealers();
    } catch (err) {
      console.error('Error deleting dealer:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white flex justify-between items-center rounded-lg bxshadowhd mrgall py-3 padlr15 border">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Building2 className="w-6 h-6" /> Dealer Maintenance
        </h1>
        <div className="flex gap-2">
          <button onClick={() => { setPage(1); fetchDealers(); }}
            className="flex items-center gap-1 px-3 py-2 border rounded-md text-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
            <Plus className="w-4 h-4" /> Add Dealer
          </button>
        </div>
      </div>

      <div className="mrgall">
        <form onSubmit={handleSearch} className="flex gap-2">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, code, contact…"
            className="flex-1 border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          <button type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Search</button>
        </form>
      </div>

      <div className="bg-white rounded-lg bxshadowhd p-2 mrgall border">
        {loading ? <LoadingSpinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {['Code', 'Name', 'Type', 'Contact', 'Brands', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-sm font-medium text-gray-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dealers.length === 0 ? (
                  <tr><td colSpan="7" className="px-4 py-6 text-center text-gray-500">No dealers found</td></tr>
                ) : dealers.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">{d.dealer_code}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{d.dealer_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{(d.dealer_type || '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>{d.contact_person}</div>
                      <div className="text-xs text-gray-400">{d.contact_number}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {(d.supported_brands || []).slice(0, 3).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-md ${d.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {d.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(d)} className="text-blue-600 hover:text-blue-800">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteId(d.id)} className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {total > LIMIT && (
              <div className="flex justify-between items-center px-4 py-3 border-t">
                <span className="text-sm text-gray-600">{total} total</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40">Prev</button>
                  <span className="px-3 py-1 text-sm">Page {page}</span>
                  <button onClick={() => setPage(p => p + 1)} disabled={page * LIMIT >= total}
                    className="px-3 py-1 text-sm border rounded disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <DealerModal isOpen={showModal} onClose={() => setShowModal(false)} onSave={handleSave} dealer={editingDealer} />

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={confirmDelete}
        title="Delete Dealer"
        message="Are you sure you want to delete this dealer? This action cannot be undone." />
    </div>
  );
};

export default DealerMaintenance;
